import React, {
  FunctionComponent, useEffect, useState,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  XtpXdpConnectionPrivileges,
  XtpXdpConnectionProtocol,
} from 'data/xtp-xdp-connection/xtp-xdp-connection.types';
import * as actions from 'data/xtp-xdp-connection/xtp-xdp-connection.actions';
import * as selectors from 'data/xtp-xdp-connection/xtp-xdp-connection.selectors';
import * as xtpActions from 'data/xtp/xtp.actions';
import * as xdpActions from 'data/xdp/xdp.actions';
import * as xtpSelectors from 'data/xtp/xtp.selectors';
import * as xdpSelectors from 'data/xdp/xdp.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { AppToaster } from 'shared/toaster';
import { xdtpXdpConnectionColumnDefs, xdtpXdpConnectionAutoGroupColumnDef } from '../../XtpXdpConnection.types';
import { adptGridData } from '../../utils/xtpXdpConnectionGrid.adapters';
import ErrorsDialog from '../../components/ErrorsDialog';
import XtpXdpGrid from './components/XtpXdpGrid';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getXtpXdpsConnectionList(state),
  selectedXtpXdpConnection: selectors.getSelectedXtpXdpsConnection(state),
  errors: selectors.getXtpErrors(state),
  uiState: selectors.getUiState(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  xtps: xtpSelectors.getXtps(state),
  xdps: xdpSelectors.getXdps(state),
});

const mapDispatch = {
  goToXtpXdpConnectionCreate: actions.goToXtpXdpConnectionCreate,
  goToXtpXdpConnectionEdit: actions.goToXtpXdpConnectionEdit,
  goToXtpXdpConnectionView: actions.goToXtpXdpConnectionView,
  selectXtpXdpConnection: actions.selectXtpXdpConnection,
  getXtpXdpConnectionList: actions.getXtpXdpConnectionList,
  deleteXtpXtpXdpConnection: actions.deleteXtpXtpXdpConnection,
  updteXtpErrors: actions.updteXtpErrors,
  getXtps: xtpActions.getXtps,
  getXdps: xdpActions.getXdps,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const XtpXdpConnectionList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedXtpXdpConnection,
  xtps,
  xdps,
  errors,
  uiState,
  goToXtpXdpConnectionCreate,
  goToXtpXdpConnectionEdit,
  goToXtpXdpConnectionView,
  selectXtpXdpConnection,
  getXtpXdpConnectionList,
  deleteXtpXtpXdpConnection,
  getXtps,
  getXdps,
  updteXtpErrors,
}: Props) => {
  const [gridData, setGridData] = useState<XtpXdpConnectionProtocol[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(xdtpXdpConnectionColumnDefs);

  useEffect(() => {
    getXtpXdpConnectionList(deliveryFunctionId);
    getXtps(deliveryFunctionId);
    getXtps(deliveryFunctionId);
    return () => {
      AppToaster.clear();
      updteXtpErrors({ connection: '', protocols: [] });
    };
  }, [deliveryFunctionId, getXtpXdpConnectionList, getXtps, updteXtpErrors, getXdps]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
    setGridData(adptGridData(data, xtps, xdps));
  }, [data, xtps, xdps]);

  const handleRefresh = () => getXtpXdpConnectionList(deliveryFunctionId);

  const getSelectedItem = (event: SelectionChangedEvent) => {
    const nodes = event.api.getSelectedNodes();
    if (nodes.length){
      return nodes[0].data ? nodes[0].data : data.find(({ name }) => name === nodes[0].key);
    }
  }

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const item = getSelectedItem(event);
    if (item){
      selectXtpXdpConnection([item]);
    }
  };

  const handledeleteXtpXtpXdpConnection = () => {
    const xtpXdpConnectionId = selectedXtpXdpConnection?.[0]?.id;
    if (xtpXdpConnectionId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };
    const dataToDelete = data.filter((item) => item.cfid === selectedXtpXdpConnection?.[0].cfid);

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedXtpXdpConnection[0]?.cfid,
        AuditService.XTP_XDP_CONNECTION,
        AuditActionType.XTP_XDP_CONNECTION_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteXtpXtpXdpConnection(deliveryFunctionId, xtpXdpConnectionId, body, dataToDelete);
  };

  const handleCreateNew = () => goToXtpXdpConnectionCreate(history);

  const handleGoToEdit = () => goToXtpXdpConnectionEdit(history);

  const handleGoToView = () => goToXtpXdpConnectionView(history);

  const hasPrivilegeToView = privileges.includes(XtpXdpConnectionPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(XtpXdpConnectionPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(XtpXdpConnectionPrivileges.Delete);

  return (
    <div data-testid="XtpXdpConnectionList" className="GridPageContainer">
      <XtpXdpGrid
        data={gridData}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedXtpXdpConnection={selectedXtpXdpConnection[0]}
        autoGroupColumnDef={xdtpXdpConnectionAutoGroupColumnDef}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handledeleteXtpXtpXdpConnection}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
      <ErrorsDialog data={errors} wasDataUpdatedWithErrors={uiState.wasDataUpdatedWithErrors} />
    </div>
  );
};

export default withRouter(connector(XtpXdpConnectionList));
